import * as React from "react";
import type { Pathway } from "./types";

export const SELECTED_PATHWAY_STORAGE_KEY = "eduviaos.selectedPathwayId";

function readStoredPathwayId(): string | null {
  try {
    return localStorage.getItem(SELECTED_PATHWAY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredPathwayId(id: string) {
  try {
    localStorage.setItem(SELECTED_PATHWAY_STORAGE_KEY, id);
  } catch {
    // Private browsing or quota — in-memory selection still works this session.
  }
}

/** Persists the learner's active plan choice for all Learn flows (SC-16). */
export function useSelectedPathway(activePlans: Pathway[]) {
  const [selectedId, setSelectedId] = React.useState<string | null>(() => readStoredPathwayId());

  React.useEffect(() => {
    if (activePlans.length === 0) return;
    const stillValid = activePlans.some((p) => p.id === selectedId);
    if (!stillValid) {
      const fallback = activePlans[0]?.id ?? null;
      setSelectedId(fallback);
      if (fallback) writeStoredPathwayId(fallback);
    }
  }, [activePlans, selectedId]);

  const selectedPlan = activePlans.find((p) => p.id === selectedId) ?? activePlans[0] ?? null;

  const selectPlan = React.useCallback((id: string) => {
    setSelectedId(id);
    writeStoredPathwayId(id);
  }, []);

  return { selectedPlan, selectedId: selectedPlan?.id ?? null, selectPlan };
}
